// Bridges a completed ZITADEL (TIPSSSO) session into the CRM's auth surface.
//
// The CRM web app authenticates with a bearer token in localStorage + a User from
// `/auth/me`. An SSO login reuses that surface: the OIDC access token becomes the
// bearer the axios client sends, and the ID-token claims hydrate a minimal User so
// the shell renders immediately. `auth_mode=sso` records the session origin.
//
// NOTE: the backend resolving the real CRM user from the ZITADEL token (via the
// `users.sso_subject` mapping) is the remaining integration step, tracked separately.
import type { User as OidcUser } from 'oidc-client-ts';
import type { User } from '@tips-crm/shared';
import { setToken } from '../api/client.js';

export const AUTH_MODE_KEY = 'tips_crm_auth_mode';

// Why a verified SSO session was rejected (e.g. the identity has no CRM account). Stashed
// when the /auth/me probe fails so the login page can explain it instead of bouncing the
// user to a blank signed-out screen with no reason.
export const SSO_ERROR_KEY = 'tips_crm_sso_error';

/** Map ID-token profile claims onto the CRM User shape (best-effort, gated). */
export function claimsToUser(oidc: OidcUser): User {
  const p = oidc.profile;
  const email = (p.email as string | undefined) ?? (p.preferred_username as string | undefined) ?? '';
  const displayName = (p.name as string | undefined) ?? email;
  return {
    id: oidc.profile.sub,
    email,
    display_name: displayName,
    // Conservative default until the backend resolves the real grant from sso_subject.
    role: 'reseller_user',
    seller_id: null,
    status: 'active',
    created_at: new Date().toISOString(),
    sso_subject: oidc.profile.sub,
  };
}

/** Persist the SSO token + marker from a freshly loaded/renewed OIDC user. */
export function bridgeSsoSession(oidc: OidcUser): User {
  if (oidc.access_token) {
    setToken(oidc.access_token);
    localStorage.setItem(AUTH_MODE_KEY, 'sso');
  }
  return claimsToUser(oidc);
}

export function clearSsoMarker(): void {
  localStorage.removeItem(AUTH_MODE_KEY);
}

/** True when the active session was established via the SSO portal. */
export function isSsoSession(): boolean {
  return localStorage.getItem(AUTH_MODE_KEY) === 'sso';
}
